import { ChangeDetectorRef, Component, Input, Signal } from '@angular/core'
import { UserDateFormattingService } from '@app/core/_services/user-date-formatting.service'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { FormsModule } from '@angular/forms'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { SelectModule } from 'primeng/select'
import { DatePickerModule } from 'primeng/datepicker'
import {
    BookSessionsService,
    ConfirmedSessionsService,
    FIFTEEN_MINS_IN_MSECS,
} from '@app/core/_services'
import {
    ConfirmedSessionsResponse,
    Container,
    SessionPreferenceFavorites,
    User,
} from '@app/core/_models'
import { combineLatestWith, filter, Subject, take, takeUntil } from 'rxjs'
import { UserService } from '@app/core/_services/user.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { ErrorHandlerService } from '@app/core/_services/error-handler.service'
import { BookLimitCheckService } from '@app/core/_services/book-limit-check.service'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { WelcomeChecklistService } from '@app/core/_services/welcome-checklist.service'
import { RewardsReferralService } from '@app/core/_services/rewards-referral.service'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { MODAL_VERIFY_EMAIL_BOOKING } from '@app/shared/components/email-verify/email-verify.component'
import { BookingSource } from '@app/core/_services/analytics.service'
import { InputTextModule } from 'primeng/inputtext'
import { FP_SELECT_TOKEN } from '@app/focus-prime/tokens/select'
import { CommonModule } from '@angular/common'
import { FP_DATEPICKER_TOKEN } from '@app/focus-prime/tokens/datepicker'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { FP_INPUTTEXT_TOKEN } from '@app/focus-prime/tokens/inputtext'
import { ButtonModule } from 'primeng/button'

interface TimeOption {
    name: string
    value: string
}

const LATE_BOOKING_GRACE_PERIOD_MINUTES = 4

interface ScheduleSessionForm {
    date: Date | null
    selectedTimeValue: string
    recurrenceType: number
    recurrenceFrequency: number
    favoritesPreference: SessionPreferenceFavorites
    title: string | null
}

interface ScheduleFormFavoritesSelectOption {
    value: SessionPreferenceFavorites
    name: string
    icon: string
}

@Component({
    selector: 'fm-booking-hub-schedule-form',
    imports: [
        InputIconModule,
        IconFieldModule,
        CommonModule,
        InputTextModule,
        DatePickerModule,
        FormsModule,
        ButtonModule,
        FontAwesomeModule,
        ToggleSwitchModule,
        SelectModule,
    ],
    templateUrl: './booking-hub-schedule-form.component.html',
    styleUrl: './booking-hub-schedule-form.component.scss',
})
export class BookingHubScheduleFormComponent {
    @Input() minimumWaitBeforeDefaultTimeslot: number = 0
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    public inputTextToken = FP_INPUTTEXT_TOKEN
    private isWelcomeChecklistActive: Signal<boolean> =
        this.welcomeChecklistService.isWelcomeChecklistActive

    public expandSchedulePanel = false
    public expandMoreOptions = false

    // Time options
    public timeSlotValues: TimeOption[] = []
    public selectedTime: TimeOption
    public minDate: Date
    public defaultDate: Date
    public dateDisplayString: string

    // Scheduler - advanced
    faChevronUp = faChevronUp
    faChevronDown = faChevronDown
    recurType = [
        { value: 1, viewValue: 'Does Not Repeat', name: 'Do not repeat', icon: 'pi pi-circle' },
        { value: 3, viewValue: 'Repeat Weekly', name: ' Repeat weekly', icon: 'pi pi-refresh' },
        { value: 2, viewValue: 'Repeat Daily', name: ' Repeat daily', icon: 'pi pi-sun' },
    ]

    recurTimes = [
        { value: 1, viewValue: '1 Time', name: '1 Time' },
        { value: 2, viewValue: '2 Times', name: '2 Times' },
        { value: 3, viewValue: '3 Times', name: '3 Times' },
        { value: 4, viewValue: '4 Times', name: '4 Times' },
        { value: 5, viewValue: '5 Times', name: '5 Times' },
        { value: 6, viewValue: '6 Times', name: '6 Times' },
        { value: 7, viewValue: '7 Times', name: '7 Times' },
        { value: 8, viewValue: '8 Times', name: '8 Times' },
        { value: 9, viewValue: '9 Times', name: '9 Times' },
        { value: 10, viewValue: '10 Times', name: '10 Times' },
    ]

    favoritesPreferences: ScheduleFormFavoritesSelectOption[] = [
        { value: 'noPreference', name: 'No preference', icon: 'pi pi-globe' },
        { value: 'preferred', name: 'Prefer favorites', icon: 'pi pi-star' },
        { value: 'onlyFavorites', name: 'Only favorites', icon: 'pi pi-star-fill' },
    ]

    public formModel: ScheduleSessionForm = {
        title: null,
        date: null,
        selectedTimeValue: null,
        recurrenceType: 1,
        recurrenceFrequency: 1,
        favoritesPreference: this.sessionSettingsService.getSessionSettingFavoritesPreference(),
    }
    private selectedSessionDuration: number
    public user: User
    public limitRemaining: number = 0
    public confirmedSessionsArray: Container<ConfirmedSessionsResponse> =
        new Container<ConfirmedSessionsResponse>()
    public isTimevalid: boolean = true

    // Tokens
    public selectToken = FP_SELECT_TOKEN
    public datePickerToken = FP_DATEPICKER_TOKEN

    constructor(
        private cdr: ChangeDetectorRef,
        public userService: UserService,
        private dialogService: DialogService,
        private bs: BookSessionsService,
        private errorHandlerService: ErrorHandlerService,
        public bookLimitCheckService: BookLimitCheckService,
        private sessionSettingsService: SessionSettingsService,
        private welcomeChecklistService: WelcomeChecklistService,
        private rewardsReferralService: RewardsReferralService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private dateFormat: UserDateFormattingService,
    ) {
        // create timeslot values
        for (let millis = 0; millis < 1000 * 60 * 60 * 24; millis += 1000 * 60 * 15) {
            const timeString = this.dateFormat.format(millis, 'h:mma', 'UTC')
            const timeStringAmPm = this.dateFormat.format(millis, 'h:mm a', 'UTC', '12hr')
            this.timeSlotValues.push({ value: timeStringAmPm, name: timeString })
        }
    }

    ngOnInit() {
        this.userService.currentUser
            .pipe(
                combineLatestWith(this.sessionSettingsService.sessionDurationSetting$),
                filter(
                    ([currentUser, sessionDuration]) =>
                        currentUser?.timeZone != null && sessionDuration != null,
                ),
                takeUntil(this.ngUnsubscribe),
            )
            .subscribe(([currentUser, sessionDuration]) => {
                this.user = currentUser
                this.selectedSessionDuration = sessionDuration

                if (!this.defaultDate) {
                    setTimeout(() => {
                        this.formModel.date = moment
                            .tz(this.user.timeZone)
                            .set({ hour: 0, minutes: 0, seconds: 0, milliseconds: 0 })
                            .toDate()
                        this.defaultDate = this.formModel.date
                        this.minDate = this.formModel.date
                        this.dateDisplayString = moment.tz(this.user.timeZone).format('DD - MMM')
                    }, 0)
                }
            })

        this.bookLimitCheckService.currentRemaining
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((limit) => {
                this.limitRemaining = limit
            })

        this.determineFirstAvailableTimeSlot()
    }

    private hasOverlapWithSession(
        proposedStartTime: number,
        existingStartTime: number,
        existingDuration: number,
        sessionDuration: number,
    ) {
        let proposedEndTime = proposedStartTime + sessionDuration
        let existEndTime = existingStartTime + existingDuration

        if (proposedStartTime < existingStartTime && proposedEndTime <= existingStartTime) {
            return false
        } else if (proposedStartTime > existEndTime) {
            return false
        } else {
            return true
        }
    }

    private determineFirstAvailableTimeSlot() {
        this.confirmedSessionsService.confirmedSessionsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((conf_sessions: Container<ConfirmedSessionsResponse>) => {
                this.confirmedSessionsArray = conf_sessions

                let now = moment.tz(this.user.timeZone)
                now = now.seconds(0)
                now = now.milliseconds(0)
                now = now.add(this.minimumWaitBeforeDefaultTimeslot, 'minutes')

                if (now.minutes() >= 45 + LATE_BOOKING_GRACE_PERIOD_MINUTES) {
                    if (now.hours() === 23) {
                        //TODO: Verify Increment date by one
                        this.formModel.date = moment
                            .tz(this.user.timeZone)
                            .set({ hour: 0, minutes: 0, seconds: 0, milliseconds: 0 })
                            .add(1, 'day')
                            .toDate()
                    }
                    now = now.minutes(0)
                    now = now.add(1, 'hours')
                } else if (now.minutes() >= 30 + LATE_BOOKING_GRACE_PERIOD_MINUTES) {
                    now = now.minutes(45)
                } else if (now.minutes() >= 15 + LATE_BOOKING_GRACE_PERIOD_MINUTES) {
                    now = now.minutes(30)
                } else if (now.minutes() >= LATE_BOOKING_GRACE_PERIOD_MINUTES) {
                    now = now.minutes(15)
                } else {
                    now = now.minutes(0)
                }

                let now_ts = now.valueOf()

                for (let i = 0; i < this.confirmedSessionsArray.length(); i++) {
                    while (
                        this.hasOverlapWithSession(
                            now_ts,
                            this.confirmedSessionsArray.getByIndex(i).sessionTime,
                            this.confirmedSessionsArray.getByIndex(i).duration,
                            this.selectedSessionDuration,
                        )
                    ) {
                        now_ts =
                            this.confirmedSessionsArray.getByIndex(i).sessionTime +
                            Math.floor(
                                this.confirmedSessionsArray.getByIndex(i).duration /
                                    FIFTEEN_MINS_IN_MSECS,
                            ) *
                                FIFTEEN_MINS_IN_MSECS +
                            FIFTEEN_MINS_IN_MSECS
                    }
                }
                this.formModel.selectedTimeValue = this.dateFormat.format(
                    now_ts,
                    'h:mm a',
                    this.user.timeZone,
                    '12hr',
                )
                this.formModel.date = moment(now_ts).tz(this.user.timeZone).toDate()
            })
    }

    isSelectedTimeValid() {
        const sessionTime = this.buildSessionTime()
        this.isTimevalid = sessionTime >= moment().tz(this.user.timeZone).valueOf()
    }

    private getHoursFromString(parsedTimeString): number {
        let hours
        if (parsedTimeString[1].slice(-2) === 'pm') {
            hours = +parsedTimeString[0]
            if (hours !== 12) {
                hours += 12
            }
        } else {
            hours = +parsedTimeString[0]
            if (hours === 12) {
                hours = 0
            }
        }
        return hours
    }

    private getMinutesFromString(parsedTimeString): number {
        return +parsedTimeString[1].slice(0, -2)
    }

    private buildSessionTime(): number {
        const parsedTimeString = this.formModel.selectedTimeValue.split(/:/)
        const hours = this.getHoursFromString(parsedTimeString)
        const minutes = this.getMinutesFromString(parsedTimeString)
        const numSessionsToBook = this.formModel.recurrenceFrequency

        return moment
            .utc(this.formModel.date)
            .tz(this.user.timeZone)
            .hours(hours)
            .minutes(minutes)
            .valueOf()
    }

    onSubmit(formData) {
        const numSessionsToBook = this.formModel.recurrenceFrequency
        const sessionTime = this.buildSessionTime()

        this.bs
            .bookRecurringSessions(
                sessionTime,
                this.formModel.recurrenceType,
                numSessionsToBook,
                this.user.timeZone,
                this.formModel.title,
                this.confirmedSessionsArray,
                this.selectedSessionDuration,
                this.formModel.favoritesPreference,
                this.sessionSettingsService.getSessionSettingActivityType(),
                this.sessionSettingsService.quietModeEnabled(),
                BookingSource.BookingHubScheduler,
            )
            .pipe(take(1))
            .subscribe(
                (sessions) => {
                    let parsedRes = this.bs.parseBookResponse(sessions)
                    let message: string = ''

                    if (parsedRes['numSuccess'] > 0) {
                        message = 'Booking saved!'

                        if (parsedRes['numSuccess'] > 1) {
                            message = 'Bookings saved!'
                        }
                    }

                    if (this.user.emailVerified === false) {
                        this.dialogService
                            .openEmailVerificationDialog(MODAL_VERIFY_EMAIL_BOOKING)
                            .afterClosed()
                            .pipe(take(1))
                            .subscribe(() => {
                                this.processParsedResult(parsedRes)
                            })
                    } else {
                        this.processParsedResult(parsedRes)
                    }
                },
                (error) => {
                    this.errorHandlerService.handleErrorResponse(error)
                },
            )
    }

    processParsedResult(parsedRes) {
        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (
            !this.isWelcomeChecklistActive() &&
            !this.user.properties.viewedSegmentationSurveyModal &&
            this.user.compSessions >= 3
        ) {
            this.dialogService.openSegmentationSurveyModal()
        } else if (
            !this.isWelcomeChecklistActive() &&
            this.rewardsReferralService.shouldSeeReferralPrompt(this.user)
        ) {
            this.dialogService.openReferralPromptModal()
        }
    }

    selectedTimeSlot(e) {
        this.isSelectedTimeValid()
    }

    // selectedDuration(e) {
    //     this.formModel.duration = e

    //     if (!this.formChanged) {
    //         this.determineFirstAvailableTimeSlot()
    //     }
    // }

    selectedRecurrenceType(e) {
        if (e != 1) {
            if (this.limitRemaining <= 10) {
                this.formModel.recurrenceFrequency = this.limitRemaining
            } else {
                this.formModel.recurrenceFrequency = 10
            }
        } else {
            this.formModel.recurrenceFrequency = 1
        }
    }

    selectedFavoritesPreference(e) {
        //this.formModel.favoritesPreference = e.value
    }

    selectedRecurFrequency(e) {
        //this.formModel.recurrenceFrequency = e.value
    }

    public onDateSelected(e) {
        this.dateDisplayString = moment.utc(e).tz(this.user.timeZone).format('DD - MMM')
        this.isSelectedTimeValid()
        this.cdr.detectChanges()
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
